import { useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace`;

function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function useWebAuthn() {
  const [loading, setLoading] = useState(false);
  const [supported] = useState(
    () => typeof window !== 'undefined' && !!window.PublicKeyCredential
  );

  const checkPlatformAuthenticator = useCallback(async () => {
    if (!supported) return false;
    try {
      return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  }, [supported]);

  const checkAvailable = useCallback(async (email) => {
    try {
      const { data } = await axios.get(`${API}/webauthn/check/${encodeURIComponent(email)}`);
      return data.available;
    } catch {
      return false;
    }
  }, []);

  const register = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const { data: options } = await axios.post(`${API}/webauthn/register/options`, {}, { headers });

      const publicKeyOptions = {
        ...options,
        challenge: Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        user: {
          ...options.user,
          id: Uint8Array.from(atob(options.user.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        },
        excludeCredentials: (options.excludeCredentials || []).map(c => ({
          ...c,
          id: Uint8Array.from(atob(c.id.replace(/-/g, '+').replace(/_/g, '/')), c2 => c2.charCodeAt(0)),
        })),
      };

      const credential = await navigator.credentials.create({ publicKey: publicKeyOptions });

      const body = {
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
          attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
        },
      };

      const { data: result } = await axios.post(`${API}/webauthn/register/verify`, body, { headers });
      setLoading(false);
      return { success: true, credential_id: result.credential_id };
    } catch (err) {
      setLoading(false);
      const msg = err?.response?.data?.detail || err.message || 'Biometric registration failed';
      return { success: false, error: msg };
    }
  }, []);

  const authenticate = useCallback(async (email) => {
    setLoading(true);
    try {
      const { data: options } = await axios.post(`${API}/webauthn/authenticate/options`, { email });
      const userId = options._user_id;

      const publicKeyOptions = {
        ...options,
        challenge: Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        allowCredentials: (options.allowCredentials || []).map(c => ({
          ...c,
          id: Uint8Array.from(atob(c.id.replace(/-/g, '+').replace(/_/g, '/')), c2 => c2.charCodeAt(0)),
        })),
      };
      delete publicKeyOptions._user_id;

      const credential = await navigator.credentials.get({ publicKey: publicKeyOptions });

      const body = {
        user_id: userId,
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: arrayBufferToBase64url(credential.response.authenticatorData),
          signature: arrayBufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle
            ? arrayBufferToBase64url(credential.response.userHandle)
            : null,
        },
      };

      const { data: result } = await axios.post(`${API}/webauthn/authenticate/verify`, body);
      setLoading(false);
      return { success: true, token: result.token, user: result.user };
    } catch (err) {
      setLoading(false);
      const msg = err?.response?.data?.detail || err.message || 'Biometric login failed';
      return { success: false, error: msg };
    }
  }, []);

  return { supported, loading, checkPlatformAuthenticator, checkAvailable, register, authenticate };
}
